// Footer Configuration File
// This file contains all footer links, company info, and social media links for easy content management

// URL Constants for easy maintenance
export const URLS = {
  // Company URLs
  contactSales: 'https://sangoma.com/contact-sales/',
  findPartner: 'https://sangoma.com/partners/find-a-local-partner/',
  aboutUs: 'https://sangoma.com/company/about-us/',
  investorRelations: 'https://sangoma.com/company/investor-relations/',
  leadership: 'https://sangoma.com/company/leadership/',
  careers: 'https://sangoma.com/company/careers/',

  // Legal URLs
  termsOfUse: 'https://sangoma.com/legal/terms-of-use/',
  cookiePolicy: 'https://sangoma.com/legal/cookie-policy/',
  privacyPolicy: 'https://sangoma.com/legal/privacy-policy/',
  sitemap: 'https://sangoma.com/sitemap_index.xml',
  contactUs: 'https://sangoma.com/contact-us/',
  legalDocuments: 'https://sangoma.com/legal/important-documents/',

  // Products & Solutions URLs
  communicationsPlatform: 'https://sangoma.com/products/communications-platform/',
  managedNetwork: 'https://sangoma.com/products/managed-network-and-connectivity/',
  managedSecurity: 'https://sangoma.com/products/managed-security/',
  openSource: 'https://sangoma.com/products/open-source/',
  phonesHardware: 'https://sangoma.com/products/phones-and-hardware/',

  // Resources URLs
  blog: 'https://sangoma.com/blog/',
  events: 'https://sangoma.com/resources/events/',
  successStories: 'https://sangoma.com/success-stories/',
  helpCenter: 'https://help.sangoma.com/community/s/',
  pressReleases: 'https://sangoma.com/company/press-releases/',

  // Social Media URLs
  phone: 'tel:+1-877-344-4861',
  facebook: 'https://www.facebook.com/Sangoma/',
  twitter: 'https://x.com/sangoma',
  youtube: 'https://www.youtube.com/user/SangomaTechnologies',
  linkedin: 'https://www.linkedin.com/company/sangoma',
} as const;

export interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
}

export interface LinkCategory {
  title: string;
  links: FooterLink[];
}

export interface SocialLink {
  platform: string;
  url: string;
  iconPath: string;
  label: string;
}

export interface CompanyInfo {
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
}

// Company Information
export const companyInfo: CompanyInfo = {
  name: 'Sangoma Technologies',
  description: 'Sangoma Meet: Simple Video Calls & Meetings',
  address: '100 Renfrew Drive, Suite 100, Markham, ON L3R 9R6, Canada',
  phone: '+1 (905) 474-1990',
  email: 'info@sangoma.com',
};

// Footer Link Categories
// Design update (#14): Reordered to match design layout
// Row 1: START NOW | COMPANY | PRODUCTS & SOLUTIONS
// Row 2: LEGAL | RESOURCES | HELP AND SUPPORT
export const linkCategories: LinkCategory[] = [
  // Row 1
  {
    title: 'START NOW',
    links: [
      { label: 'Book a meeting', url: URLS.contactSales, external: true },
      { label: 'Find a partner', url: URLS.findPartner, external: true },
    ],
  },
  {
    title: 'COMPANY',
    links: [
      { label: 'About Us', url: URLS.aboutUs, external: true },
      { label: 'Investor Relations', url: URLS.investorRelations, external: true },
      { label: 'Leadership Teams', url: URLS.leadership, external: true },
      { label: 'Work with us', url: URLS.careers, external: true },
    ],
  },
  {
    title: 'PRODUCTS & SOLUTIONS',
    links: [
      { label: 'Managed Communications', url: URLS.communicationsPlatform, external: true },
      { label: 'Managed Network & Connectivity', url: URLS.managedNetwork, external: true },
      { label: 'Managed Security', url: URLS.managedSecurity, external: true },
      { label: 'Open Source', url: URLS.openSource, external: true },
      { label: 'Phone & Hardware', url: URLS.phonesHardware, external: true },
    ],
  },
  // Row 2
  {
    title: 'LEGAL',
    links: [
      { label: 'Terms of use', url: URLS.termsOfUse, external: true },
      { label: 'Cookie Policy', url: URLS.cookiePolicy, external: true },
      { label: 'Privacy policy', url: URLS.privacyPolicy, external: true },
      { label: 'Sitemap', url: URLS.sitemap, external: true },
      { label: 'Contact Us', url: URLS.contactUs, external: true },
    ],
  },
  {
    title: 'RESOURCES',
    links: [
      { label: 'Sangoma Blog', url: URLS.blog, external: true },
      { label: 'Events', url: URLS.events, external: true },
      { label: 'Success Stories', url: URLS.successStories, external: true },
      { label: 'Help Center', url: URLS.helpCenter, external: true },
      { label: 'Press Releases', url: URLS.pressReleases, external: true },
    ],
  },
  {
    title: 'HELP AND SUPPORT',
    links: [
      { label: 'Help Center', url: URLS.helpCenter, external: true },
      { label: 'Policies & Legal', url: URLS.legalDocuments, external: true },
    ],
  },
];

// Social Media Links
export const socialLinks: SocialLink[] = [
  {
    platform: 'Phone',
    url: URLS.phone,
    iconPath: '',
    label: 'Call us at +1-877-344-4861',
  },
  {
    platform: 'Facebook',
    url: URLS.facebook,
    iconPath: '',
    label: 'Follow us on Facebook',
  },
  {
    platform: 'Twitter',
    url: URLS.twitter,
    iconPath: '',
    label: 'Follow us on Twitter',
  },
  {
    platform: 'YouTube',
    url: URLS.youtube,
    iconPath: '',
    label: 'Subscribe to our YouTube channel',
  },
  {
    platform: 'LinkedIn',
    url: URLS.linkedin,
    iconPath: '',
    label: 'Follow us on LinkedIn',
  },
];

// Footer Text Configuration
// Design update (#13): "Sangoma" text should be bold - rendered via JSX <strong> in Footer component
export const footerText = {
  connectTitle: "How can we help? Let's connect",
  connectDescription:
    'Get in touch with our team for support, sales inquiries, or partnership opportunities.',
  companyName: 'Sangoma',
  companyDescriptionBody:
    ' is the leading end-to-end managed services provider with a proprietary cloud communications and network platform for multi-location businesses.',
};
